/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { gmbGetReviewStats, gmbListReviews, gmbListAccounts, gmbListLocations, gmbListPosts } from 'src/services/googleMyBusiness';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { fetchAllNeedsResponse } from 'src/utils/gmbNeedsResponse';
import { dispatch, store } from '../store';
import { setSelectedGoogleAccount } from './user';

const initialState = {
    // Cache keyed by `${practiceId}_${accountId}_${locationId}`
    reviewStats: {},
    reviews: {},
    needsResponse: {},
    posts: {},
    // Loading state for background syncs
    syncing: false,
    // Last sync timestamp per location
    lastSyncTime: {},
    // True while prefetchGoogleReviewsOnDashboardLoad is in flight for any practice.
    // LaunchpadReviewsCarousel reads this flag and waits rather than firing a
    // duplicate gmbGetReviewStats while the prefetch is already running.
    // A module-level sentinel prevents re-entrant calls within the same session;
    // this Redux flag is purely for communicating "wait" to carousel subscribers.
    prefetchInFlight: false,
};

// Helper to generate cache key
const getCacheKey = (practiceId, accountId, locationId) =>
    `${practiceId}_${accountId}_${locationId}`;

export const slice = createSlice({
    name: 'reviewStatsCache',
    initialState,
    reducers: {
        setReviewStatsCache(state, action) {
            const { key, data } = action.payload;
            state.reviewStats[key] = data;
            state.lastSyncTime[key] = Date.now();
        },
        setReviewsCache(state, action) {
            const { key, data } = action.payload;
            state.reviews[key] = data;
        },
        setNeedsResponseCache(state, action) {
            const { key, data } = action.payload;
            state.needsResponse[key] = data;
        },
        setPostsCache(state, action) {
            const { key, data } = action.payload;
            state.posts[key] = data;
        },
        setSyncing(state, action) {
            state.syncing = action.payload;
        },
        clearCache(state) {
            state.reviewStats = {};
            state.reviews = {};
            state.needsResponse = {};
            state.posts = {};
            state.lastSyncTime = {};
        },
        removeLocationCache(state, action) {
            const { key } = action.payload;
            delete state.reviewStats[key];
            delete state.reviews[key];
            delete state.needsResponse[key];
            delete state.posts[key];
            delete state.lastSyncTime[key];
        },
        setPrefetchInFlight(state, action) {
            state.prefetchInFlight = Boolean(action.payload);
        },
    },
});

export const {
    setReviewStatsCache,
    setReviewsCache,
    setNeedsResponseCache,
    setPostsCache,
    setSyncing,
    clearCache,
    removeLocationCache,
    setPrefetchInFlight,
} = slice.actions;

export default slice.reducer;

// Module-level single-flight sentinel (not Redux-persisted, resets on every page load).
// Prevents two concurrent calls to prefetchGoogleReviewsOnDashboardLoad for the same
// practice (e.g. DashboardLayout + LaunchpadView both dispatch it on mount).
// JS is single-threaded, so the check+set between two thunks that start synchronously
// is atomic: the first thunk sets this before its first await; the second thunk sees it
// and returns early before dispatching setPrefetchInFlight(true).
let _prefetchInflightPracticeId = null;

// Comprehensive prefetch that loads accounts, locations, and all review data
// This mimics what happens when opening the Google Reviews page
export function prefetchGoogleReviewsOnDashboardLoad(practiceId) {
    return async () => {
        if (!practiceId) return;
        if (!hasFeatureAccess('google-reviews')) return;

        // Single-flight guard: if a prefetch is already in progress for this practice,
        // return without dispatching a second set of API calls.
        if (_prefetchInflightPracticeId === String(practiceId)) return;
        _prefetchInflightPracticeId = String(practiceId);
        dispatch(slice.actions.setPrefetchInFlight(true));

        try {
            // Get saved account from Redux
            const state = store.getState();
            let savedGoogleAccount = state.user?.selectedGoogleAccount;

            // RCA6 — stale-GMB-id 404 on in-app practice switch.
            // selectedGoogleAccount is never reset when practiceId changes, so after a
            // switch it still holds the PREVIOUS practice's account/location ids.
            // LaunchpadReviewsCarousel / LaunchpadReviews read those ids and fire
            // practices/<new>/googlemybusiness/<old>/locations/<old>/review_stats/ → 404.
            // Clear the stale selection BEFORE resolving this practice's account so the
            // carousel's hasGoogle goes false and it early-returns until the new
            // practice's account lands (this thunk re-stamps it below).
            // Only clear when the saved account is stamped with a DIFFERENT practice —
            // clearing for the same practice would needlessly flash the carousel.
            if (
                savedGoogleAccount?.accountId
                && savedGoogleAccount._practiceId != null
                && String(savedGoogleAccount._practiceId) !== String(practiceId)
            ) {
                dispatch(setSelectedGoogleAccount(null));
                savedGoogleAccount = null;
            }

            // 1. Load all accounts
            const accountsResponse = await gmbListAccounts(practiceId);
            const accounts = Array.isArray(accountsResponse?.results)
                ? accountsResponse.results
                : Array.isArray(accountsResponse) ? accountsResponse : [];

            if (accounts.length === 0) {
                return; // No accounts to prefetch
            }

            // 2. Load locations for all accounts in parallel
            const locationPromises = accounts.map(async (account) => {
                try {
                    const locResponse = await gmbListLocations(practiceId, account.id);
                    const locations = Array.isArray(locResponse?.results)
                        ? locResponse.results
                        : Array.isArray(locResponse) ? locResponse : [];
                    // Filter connected locations
                    const connectedLocations = locations.filter((l) => l?.is_connected !== false);
                    return { account, locations: connectedLocations };
                } catch {
                    return { account, locations: [] };
                }
            });

            const accountsWithLocations = await Promise.all(locationPromises);

            // Flatten to get all location entries
            const allLocations = accountsWithLocations.flatMap(({ account, locations }) =>
                locations.map((location) => ({ account, location }))
            );

            if (allLocations.length === 0) {
                return; // No locations to prefetch
            }

            // 3. Determine which account/location to use
            let accountToUse = null;
            let locationToUse = null;

            if (savedGoogleAccount?.accountId) {
                // Try to find saved account/location
                const savedEntry = allLocations.find(
                    (entry) => entry.account?.id === savedGoogleAccount.accountId
                );
                if (savedEntry) {
                    accountToUse = savedEntry.account;
                    // Find saved location or use first one for this account
                    const accountLocations = allLocations.filter(
                        (x) => x.account?.id === savedEntry.account.id
                    );
                    if (savedGoogleAccount.locationId) {
                        // Match by database id (savedGoogleAccount.locationId stores the DB id)
                        const savedLocation = accountLocations.find(
                            (x) => x.location?.id === savedGoogleAccount.locationId
                        );
                        locationToUse = savedLocation?.location || accountLocations[0]?.location || null;
                    } else {
                        locationToUse = accountLocations[0]?.location || null;
                    }
                }
            }

            // If no saved account found, use first available
            if (!accountToUse && allLocations.length > 0) {
                accountToUse = allLocations[0].account;
                locationToUse = allLocations[0].location;
            }

            if (!accountToUse || !locationToUse) {
                return; // No valid account/location to prefetch
            }

            // Use database `id` for API calls (not location_id which is Google's identifier)
            const locationDbId = locationToUse.id;

            // 4. Save selected account to Redux if not already saved
            if (!savedGoogleAccount?.accountId || savedGoogleAccount.accountId !== accountToUse.id) {
                dispatch(setSelectedGoogleAccount({
                    accountId: accountToUse.id,
                    locationId: locationDbId,
                    // Stamp the owning practice so a later prefetch after a practice
                    // switch can detect this selection is stale (see RCA6 clear above).
                    // Mirrors the choicesData._practiceId pattern (practiceTreatment.js).
                    _practiceId: practiceId,
                }));
            }

            // 5. Prefetch all review data and posts for this location
            const key = getCacheKey(practiceId, accountToUse.id, locationDbId);

            // Dashboard-load prefetch only needs the launchpad's top-5 preview plus the
            // server's true count — not the full unreplied list (that's what made this
            // prefetch fan out to up to 21 requests on a location with >50 unreplied
            // reviews). One page of 5, no escalation; the reviews page (ManageBusiness)
            // paginates the rest itself, on demand, via the same shared helper.
            const [reviewStatsData, reviewsData, needsResponseResult, postsData] = await Promise.all([
                gmbGetReviewStats(practiceId, accountToUse.id, locationDbId).catch(() => null),
                gmbListReviews(practiceId, accountToUse.id, locationDbId, { limit: 25, offset: 0 }).catch(() => null),
                fetchAllNeedsResponse(practiceId, accountToUse.id, locationDbId, { pageSize: 5, maxPages: 1 }).catch(() => null),
                gmbListPosts(practiceId, accountToUse.id, locationDbId, { ordering: '-create_time' }).catch(() => null),
            ]);

            if (reviewStatsData) {
                dispatch(setReviewStatsCache({ key, data: reviewStatsData }));
            }
            if (reviewsData) {
                dispatch(setReviewsCache({ key, data: reviewsData }));
            }
            if (needsResponseResult) {
                dispatch(setNeedsResponseCache({
                    key,
                    data: {
                        data: needsResponseResult.data,
                        count: needsResponseResult.count,
                        complete: needsResponseResult.complete,
                        loadedPages: needsResponseResult.loadedPages,
                    },
                }));
            }
            if (postsData) {
                const postsArr = Array.isArray(postsData?.results)
                    ? postsData.results
                    : Array.isArray(postsData) ? postsData : [];
                dispatch(setPostsCache({ key, data: postsArr }));
            }

        } catch (err) {
            // Silently fail for prefetch - don't disrupt user experience
            console.warn('Prefetch Google reviews on dashboard load failed:', err);
        } finally {
            // Release the single-flight lock and signal carousel subscribers that
            // the prefetch is done (success or error). Subscribers re-check the cache;
            // if it was populated they skip their own fetch, otherwise they fall back.
            _prefetchInflightPracticeId = null;
            dispatch(slice.actions.setPrefetchInFlight(false));
        }
    };
}

// Thunk to sync review stats in background (with syncing indicator)
export function syncReviewStatsInBackground(practiceId, accountId, locationId, onComplete) {
    return async () => {
        if (!practiceId || !accountId || !locationId) return;
        if (!hasFeatureAccess('google-reviews')) return;

        const key = getCacheKey(practiceId, accountId, locationId);

        dispatch(setSyncing(true));

        try {
            const data = await gmbGetReviewStats(practiceId, accountId, locationId);
            dispatch(setReviewStatsCache({ key, data }));
            if (onComplete) onComplete(true, data);
        } catch (err) {
            console.warn('Background sync review stats failed:', err);
            if (onComplete) onComplete(false, null);
        } finally {
            dispatch(setSyncing(false));
        }
    };
}

// Selector to get cached review stats for a location
export const selectCachedReviewStats = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.reviewStats?.[key] || null;
};

// Selector to get cached reviews for a location
export const selectCachedReviews = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.reviews?.[key] || null;
};

// Selector to get cached needs response for a location
export const selectCachedNeedsResponse = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.needsResponse?.[key] || null;
};

// Selector to get cached posts for a location
export const selectCachedPosts = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.posts?.[key] || null;
};

// Selector to check if we have cached data
export const selectHasCachedData = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return false;
    const key = getCacheKey(practiceId, accountId, locationId);
    return !!(
        state.reviewStatsCache?.reviewStats?.[key] ||
        state.reviewStatsCache?.reviews?.[key] ||
        state.reviewStatsCache?.needsResponse?.[key]
    );
};

// Selector to get syncing state
export const selectIsSyncing = (state) => state.reviewStatsCache?.syncing || false;

// Selector to get last sync time
export const selectLastSyncTime = (state, practiceId, accountId, locationId) => {
    if (!practiceId || !accountId || !locationId) return null;
    const key = getCacheKey(practiceId, accountId, locationId);
    return state.reviewStatsCache?.lastSyncTime?.[key] || null;
};

// Selector: true while prefetchGoogleReviewsOnDashboardLoad is in flight.
// Components (e.g. LaunchpadReviewsCarousel) watch this and skip their own
// GMB fetch when the prefetch is already running.
export const selectPrefetchInFlight = (state) => state.reviewStatsCache?.prefetchInFlight || false;

// Helper to get cache key (exported for use in components)
export { getCacheKey };
